import { SettlementCalendar } from "./SettlementCalendar";
function roundMoney(value, currency) {
    return currency === "VND" ? Math.round(value) : value;
}
export class FillSettlement {
    oms;
    pms;
    sim;
    rules;
    mktSeq = 0;
    constructor(oms, pms, sim, rules) {
        this.oms = oms;
        this.pms = pms;
        this.sim = sim;
        this.rules = rules;
    }
    /** S2: swap the rules reference (prospective fills only; booked history untouched). */
    updateRules(rules) {
        this.rules = rules;
    }
    settle(books, args) {
        const { symbol, side, quantity, price, timestamp } = args;
        const stored = args.orderId ? this.oms.getOrder(args.orderId) : undefined;
        const pseudo = stored ?? {
            id: `mkt_${++this.mktSeq}`,
            clientOrderId: `mkt-cli-${this.mktSeq}`,
            symbol,
            side,
            type: "market",
            status: "filled",
            quantity,
            filledQuantity: quantity,
            timeInForce: "GTC",
            createdAt: timestamp,
            updatedAt: timestamp,
        };
        const net = this.pms.getPosition(symbol);
        const opposite = net && ((net.side === "long") === (side === "sell")) ? net : undefined;
        const ticket = this.sim.executeFill({
            order: pseudo,
            basePrice: price,
            quantity,
            timestamp,
            positionBasis: opposite ? { entryPrice: opposite.entryPrice, side: opposite.side } : undefined,
            exitReason: args.exitReason,
        }, this.rules);
        this.flagAmbiguity(ticket, pseudo, args.bar);
        if (opposite) {
            const closeQty = Math.min(quantity, opposite.quantity);
            const res = this.pms.closePosition(opposite.id, closeQty, ticket.fillPrice, timestamp, args.exitReason, {
                enforceAvailable: false,
            });
            if (this.rules.profile !== "VN_SPOT") {
                books.account.balance = roundMoney(books.account.balance + res.realizedPnl, books.account.currency);
            }
            books.analytics.recordClosedTrade({
                timestamp,
                // S5: `realizedPnl` is the price difference only (gross). The trade's NET
                // figure needs BOTH legs' fees: this exit's, plus the entry fee already
                // taken from cash when the position opened. Before S5 the entry fee was in
                // neither place, so a per-trade net could never reconcile with the cash
                // balance - the gap was exactly the entry commission.
                realizedPnl: res.realizedPnl,
                commission: roundMoney(res.entryFees + (ticket.commission * closeQty) / quantity, books.account.currency),
                tax: roundMoney((ticket.tax * closeQty) / quantity, books.account.currency),
                symbol,
                side: opposite.side,
                quantity: closeQty,
                entryPrice: opposite.entryPrice,
                exitPrice: ticket.fillPrice,
                exitReason: args.exitReason,
            });
            const remainder = quantity - closeQty;
            if (remainder > 0) {
                this.openFresh(symbol, side, remainder, ticket.fillPrice, timestamp, ticket.commission, ticket.tax);
            }
        }
        else {
            this.openFresh(symbol, side, quantity, ticket.fillPrice, timestamp, ticket.commission, ticket.tax);
        }
        this.applyCashflow(books, side, quantity, ticket);
        books.history.push(ticket);
        return ticket;
    }
    openFresh(symbol, side, quantity, price, timestamp, commission, tax) {
        this.pms.openPosition({
            symbol,
            side: side === "buy" ? "long" : "short",
            quantity,
            price,
            timestamp,
            // S5: these fees are charged to cash right now, so they belong to the round
            // trip that will close this position - not lost between the two.
            entryFees: commission + tax,
            settlementDate: this.rules.settlementDays > 0
                ? SettlementCalendar.calculateSettlementTimestamp(timestamp, this.rules.settlementDays)
                : undefined,
        });
    }
    applyCashflow(books, side, quantity, ticket) {
        const { currency } = books.account;
        if (this.rules.profile === "VN_SPOT") {
            if (side === "buy") {
                books.account.balance = roundMoney(books.account.balance - quantity * ticket.fillPrice - ticket.commission, currency);
            }
            else {
                books.account.balance = roundMoney(books.account.balance + quantity * ticket.fillPrice - ticket.commission - ticket.tax, currency);
            }
        }
        else {
            books.account.balance = roundMoney(books.account.balance - ticket.commission - ticket.tax, currency);
        }
    }
    flagAmbiguity(ticket, order, bar) {
        if (!order.parentOrderId || !bar)
            return;
        const siblings = this.oms.listOrders().filter((o) => o.parentOrderId === order.parentOrderId);
        const tp = siblings.find((o) => o.type === "limit");
        const sl = siblings.find((o) => o.type === "stop" || o.type === "trailing_stop");
        if (tp?.price == null || sl?.stopPrice == null)
            return;
        const closedSide = ticket.side === "buy" ? "short" : "long";
        if (this.sim.checkIntrabarAmbiguity(tp.price, sl.stopPrice, bar, closedSide)) {
            ticket.ambiguousIntrabarFill = true;
            ticket.tieBreakReason = "CONSERVATIVE_SL_PRIORITY";
        }
    }
}
